import type { PublicPriceTier } from '@repo/api-contracts';
import { ChevronRight } from 'lucide-react';
import { type ReactElement, useCallback, useState } from 'react';
import { activeTierOf } from '@/entities/product';
import { formatCount, formatKrw } from '@/shared/lib';
import { cn } from '@/shared/ui';
import { EditorDialog } from './editor-dialog';

interface PriceTierButtonProps {
  /** minQty 오름차순. */
  readonly tiers: readonly PublicPriceTier[];
  /** 지금 고른 수량 — 속한 구간을 강조한다. */
  readonly qty: number | null;
}

const rangeLabel = (tier: PublicPriceTier, next: PublicPriceTier | undefined): string =>
  next === undefined
    ? `${formatCount(tier.minQty)}개 이상`
    : `${formatCount(tier.minQty)} ~ ${formatCount(next.minQty - 1)}개`;

/**
 * [수량별 가격] 글자 링크 — 상품 상세의 수량별 가격 표와 같은 표(굵은 윗선·얇은 줄·선택 구간 강조)를 대화상자로 보인다.
 */
export const PriceTierButton = ({ tiers, qty }: PriceTierButtonProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const active = activeTierOf(tiers, qty);

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        className="inline-flex cursor-pointer items-center gap-0.5 text-sm font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => setOpen(true)}
      >
        수량별 가격 보기
        <ChevronRight aria-hidden className="size-4" strokeWidth={1.5} />
      </button>
      <EditorDialog open={open} title="수량별 가격" onClose={close}>
        <table className="w-full border-t border-foreground text-sm">
          <thead className="text-left text-muted-foreground">
            <tr className="border-b border-foreground/10">
              <th scope="col" className="px-3 py-3 font-medium">
                수량 구간
              </th>
              <th scope="col" className="px-3 py-3 text-right font-medium">
                개당 가격
              </th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier, index) => {
              const isActive = tier === active;
              return (
                <tr
                  key={tier.minQty}
                  aria-current={isActive ? 'true' : undefined}
                  className={cn(
                    'border-b border-foreground/10',
                    isActive && 'bg-muted/70 font-semibold',
                  )}
                >
                  <td className="px-3 py-3.5">
                    <span className="flex items-center gap-2">
                      {rangeLabel(tier, tiers[index + 1])}
                      {/* 지금 수량의 구간 — 색만으로 구분하지 않게 글자 표식을 함께 단다. */}
                      {isActive && (
                        <span className="bg-brand-lime px-1.5 py-0.5 text-[11px] font-semibold text-black">
                          선택 수량
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-3.5 text-right tabular-nums">
                    {formatKrw(tier.unitPrice)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="mt-4 text-xs text-muted-foreground">
          예상 가격이며, 인쇄 방식·도수에 따라 확정 견적에서 달라질 수 있습니다.
        </p>
      </EditorDialog>
    </>
  );
};
